import { AsyncPipe, NgIf } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { saveAs } from 'file-saver';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ListboxModule } from 'primeng/listbox';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { BaseUtenteComponent } from 'src/app/components/base-utente/base-utente.component';
import { DataSearch } from 'src/app/models/data-search';
import { ColumnExportDto } from 'src/app/models/gestione/dto/column-export-dto';
import { EntityExportDto } from 'src/app/models/gestione/dto/entity-export-dto';
import { ExportListPars } from 'src/app/models/gestione/list-export-pars';
import { LookupDto } from 'src/app/models/lookup/lookup-dto';
import { AppManagerService } from 'src/app/services/app-manager.service';
import { DataService } from 'src/app/services/data.service';
import { ListExportService } from 'src/app/services/list-export.service';
import { GenLookupService } from 'src/app/services/lookup/gen-lookup.service';
import { Resources, TranslateService } from 'src/app/services/translate.service';
import { TranslateItemPipe } from '../../../../pipes/translate-item.pipe';
import { safeTakeUntilDestroyed } from '../../../../utils/util';

/** Template di esportazione per la tendina, con le colonne già lette dal JSON */
interface ExportTemplateOption extends LookupDto {
  columns: Record<string, boolean>;
}

@Component({
  selector: 'app-list-export',
  templateUrl: './list-export.component.html',
  styles: ``,
  standalone: true,
  imports: [
    AsyncPipe,
    ButtonModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    ListboxModule,
    NgIf,
    ReactiveFormsModule,
    TranslateItemPipe
  ]
})
export class ListExportComponent extends BaseUtenteComponent {
  T$: Observable<Resources> = this.translateService.translatedItems$(['generic.', 'listexport.']);
  moadlListExport$: Observable<ExportListPars>;
  exportTemplateList$: Observable<ExportTemplateOption[]>;
  columnList$: Observable<ColumnExportDto[]>;
  visible: boolean = false;
  dataForm: FormGroup;
  dataSearch: DataSearch;
  columnList: any;
  entityName: string;
  elaboration: boolean = false;

  constructor(
    private readonly dataService: DataService,
    private readonly translateService: TranslateService,
    private readonly listExportService: ListExportService,
    private readonly fb: FormBuilder,
    public override readonly appManagerService: AppManagerService,
    private readonly genLookupService: GenLookupService
  ) {
    super(appManagerService);
  }

  onUtenteInit(): void {
    this.moadlListExport$ = this.listExportService.getSubject();

    // Recupera le colonne dell'entità alla richiesta di apertura della modale
    this.columnList$ = this.moadlListExport$.pipe(
      tap((data) => {
        this.visible = data.open;
      }),
      filter((data) => data.open),
      tap((data) => {
        this.entityName = data.entityName;

        // Data search senza paginazione
        this.dataSearch = data.dataSearch;
        delete this.dataSearch.pageFirst;
        delete this.dataSearch.pageSize;

        this.dataForm.get('fileName').setValue(this.listExportService.defaultFileName());
      }),
      switchMap((data) =>
        this.dataService.getGeneric(
          `${this.dataService.configSettings.restCommonUrl}/GetEntityExportColumns`,
          `entityName=${data.entityName}${data.nameSpace ? `&nameSpace=${data.nameSpace}` : ''}`
        )
      ),
      tap((data) => {
        this.columnList = data;
        this.dataForm.get('columns').setValue(data);
      })
    );

    this.exportTemplateList$ = this.appManagerService.initialized$.pipe(
      filter((data) => data.initialized),
      // Il backend filtra per il profilo corrente dell'utente
      switchMap(() => this.genLookupService.getGenExportTemplateLookup$(this.entityName)),
      map((templates) =>
        templates.map((t): ExportTemplateOption => ({
          ...t,
          columns: JSON.parse(String(t.columns)) as Record<string, boolean>
        }))
      )
    );

    // Costruisco il data form
    this.dataForm = this.fb.group({
      exportTemplateId: null,
      fileName: [this.listExportService.defaultFileName(), Validators.required],
      columns: [[], Validators.required]
    });

    this.dataForm
      .get('exportTemplateId')
      .valueChanges.pipe(safeTakeUntilDestroyed(this.destroyRef))
      .subscribe((data: ExportTemplateOption) => {
        const cols = this.columnList
          .map((c) => {
            c.selected = data?.columns?.[c.name] ?? false;
            return c;
          })
          .filter((c) => c.selected);
        this.dataForm.get('columns').setValue(cols, { emitEvent: false });
      });
    this.dataForm
      .get('columns')
      .valueChanges.pipe(safeTakeUntilDestroyed(this.destroyRef))
      .subscribe((_val: ColumnExportDto[]) =>
        this.dataForm.get('exportTemplateId').setValue(null, { emitEvent: false })
      );
  }

  hideModal(): void {
    this.dataForm.reset();
    this.listExportService.closeModal();
  }

  onTemplateClear(): void {}

  exportExcel(): void {
    this.elaboration = true;

    const values = this.dataForm.value;

    const data = {
      entityName: this.entityName,
      columns: values.columns,
      dataSearch: this.dataSearch,
      fileName: values.fileName
    } as EntityExportDto;

    this.listExportService
      .exportFile(`${this.dataService.configSettings.restCommonUrl}/exportgridtoexcel`, data)
      .subscribe({
        next: (blob) => {
          saveAs(blob, `${values.fileName}.xlsx`);
          this.elaboration = false;
          this.hideModal();
        },
        error: (_err) => {
          this.elaboration = false;
        }
      });
  }
}
